import { useState, useMemo, useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import api, { getList } from '../api/client';
import { batch } from '../api/endpoints';
import type { Resource, BatchOperation } from '../api/endpoints';
import type { IdentifiedRecord, VoidableRecord } from '../types/common';
import useFetchList from './useFetchList';
import { nextSequence as nextSequenceFrom } from '../utils/records';
import type { Notify } from './useNotification';

// Several modules manage two or more sibling resources behind one screen and
// merge their VOIDed rows into a single "Deleted / Deleted" tab:
//
//   Cemetery   -> plots + burials
//   Youth      -> members + programs
//   Choir      -> members + schedules + assets
//   Assets     -> assets + allocations + maintenance
//   Meetings   -> records + minutes + actions
//   Committee  -> terms + members + handovers
//
// Each loads every sibling in parallel, tags rows with a record type, splits
// ACTIVE from VOID, and issues a soft delete with a reason. That whole cycle
// lives here once.
//
//   const ledger = useMultiLedger({
//     members:  { resource: youthMembersApi,  label: 'Member',  prefix: 'YTH' },
//     programs: { resource: youthProgramsApi, label: 'Program', prefix: 'YPR' }
//   }, { notify });
//
//   ledger.active('members')   // typed as LedgerRow<YouthMember>[]
//   ledger.active('programs')  // typed as LedgerRow<YouthProgram>[]
//   ledger.save('members', payload, editingRawId)

/** Configuration for one sibling resource. */
export interface LedgerResourceConfig<T> {
  resource: Resource<T>;
  /** Shown in messages and in the Deleted "type" column. */
  label: string;
  /** Human-ID prefix, e.g. `YTH` -> `YTH-1004`. */
  prefix: string;
  /**
   * Column carrying this resource's live/void flag when it isn't `status`
   * — e.g. Choir members use `memberStatus`, assets use `assetStatus`.
   */
  statusField?: string;
  /**
   * Normalises a resource whose columns differ from the UI's field names —
   * e.g. Pastoral Care's visit requests arrive from the mobile app as
   * requestedBy / preferredDate / requestType.
   */
  map?: (row: T) => T;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/** The config map. `any` here is the standard escape hatch for a heterogeneous
 *  record whose per-key types are recovered by RowOf below. */
export type LedgerConfig = Record<string, LedgerResourceConfig<any>>;
/* eslint-enable @typescript-eslint/no-explicit-any */

/** A fetched row, tagged with which sibling resource it came from. */
export type LedgerRow<T> = T & {
  /** The database primary key, preserved because `id` gets remapped in places. */
  rawId?: number;
  /** Internal: which config key this row belongs to. */
  __key: string;
  /**
   * Internal: the row exactly as the server sent it, before any `map`. An
   * edit is sent as this row with the form laid over it — the server
   * replaces the whole record, so a form-only payload would blank every
   * column the form does not show (createdBy above all).
   */
  __raw?: Record<string, unknown>;
  /** The config's `label`, shown in the Deleted table. */
  recordType: string;
};

/** Recovers the row type for one config key, so active('members') is typed. */
export type RowOf<C extends LedgerConfig, K extends keyof C> =
  C[K] extends LedgerResourceConfig<infer T> ? LedgerRow<T> : never;

export interface UseMultiLedgerOptions {
  notify?: Notify;
}

export interface LedgerGroup<T> {
  active: T[];
  voided: T[];
}

export interface UseMultiLedgerResult<C extends LedgerConfig> {
  /** Every row across every sibling resource. */
  rows: Array<LedgerRow<IdentifiedRecord>>;
  setRows: Dispatch<SetStateAction<Array<LedgerRow<IdentifiedRecord>>>>;
  groups: Record<string, LedgerGroup<LedgerRow<IdentifiedRecord>>>;
  active: <K extends keyof C & string>(key: K) => Array<RowOf<C, K>>;
  voided: <K extends keyof C & string>(key: K) => Array<RowOf<C, K>>;
  loading: boolean;
  error: Error | null;
  /** Labels of the sibling resources whose fetch failed, so the list is partial. */
  missing: string[];
  refetch: () => Promise<void>;
  saving: boolean;
  /**
   * Pass `editingRawId` to update, omit it to create. Resolves true on success.
   * `together`: other saves that belong to this one (a transfer moving the
   * asset, archiving the current pastor) — all saved at once, or sent for
   * approval as one request.
   */
  save: <K extends keyof C & string>(
    key: K,
    payload: Partial<RowOf<C, K>>,
    editingRawId?: number | string | null,
    together?: { operations: BatchOperation[]; summary: string }
  ) => Promise<boolean>;
  nextSequence: <K extends keyof C & string>(key: K, base?: number) => string;
  voiding: LedgerRow<IdentifiedRecord> | null;
  requestVoid: (row: LedgerRow<IdentifiedRecord>) => void;
  cancelVoid: () => void;
  /** `onVoided` runs before the refetch — e.g. Cemetery frees the plot. */
  confirmVoid: (
    reason: string,
    onVoided?: (row: LedgerRow<IdentifiedRecord>) => void | Promise<void>
  ) => Promise<boolean>;
}

const stamp = (): string =>
  new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

export const useMultiLedger = <C extends LedgerConfig>(
  config: C,
  { notify }: UseMultiLedgerOptions = {}
): UseMultiLedgerResult<C> => {
  type Row = LedgerRow<IdentifiedRecord>;
  type Key = keyof C & string;

  const keys = useMemo(() => Object.keys(config) as Key[], [config]);
  const [saving, setSaving] = useState<boolean>(false);
  const [voiding, setVoiding] = useState<Row | null>(null);

  const announce = useCallback(
    (message: string, type?: 'success' | 'error') => {
      if (typeof notify === 'function') notify(message, type);
    },
    [notify]
  );

  // Sibling failures used to be caught into an empty array, so a dead endpoint
  // looked exactly like "no records yet" — and, worse, restarted nextSequence at
  // the base. The ones that failed are named and reported instead.
  const [missing, setMissing] = useState<string[]>([]);

  const { data: rows, setData: setRows, loading, error, refetch } = useFetchList<Row>(
    async (p, options) => {
      const settled = await Promise.allSettled(
        keys.map((k) => api.get(config[k].resource.url(), options))
      );

      // A superseded load aborts its requests — StrictMode's double effect in
      // development, and every refetch after a save. Those rejections are not
      // failures, and useFetchList discards this result anyway, so bail out
      // before reporting anything. Without this the user was told the data had
      // failed to load every single time it loaded fine.
      if (options?.signal?.aborted) return [];

      const isAbort = (r: PromiseSettledResult<unknown>) =>
        r.status === 'rejected' && (r.reason as Error | undefined)?.name === 'AbortError';

      const failed = keys.filter((_, i) => settled[i].status === 'rejected' && !isAbort(settled[i]));
      setMissing(failed.map((k) => config[k].label));
      if (failed.length) {
        const names = failed.map((k) => config[k].label.toLowerCase()).join(', ');
        announce(`Could not load ${names}. What you see is incomplete — reload to try again.`, 'error');
      }

      return keys.flatMap((k, i) => {
        const outcome = settled[i];
        if (outcome.status !== 'fulfilled') return [];
        const normalise = config[k].map;
        return getList<Record<string, unknown>>(outcome.value).map((row) => ({
          ...(normalise ? normalise(row) : row),
          rawId: row.id as number | undefined,
          __key: k,
          __raw: row,
          recordType: config[k].label
        })) as Row[];
      });
    }
  );

  // Grouped once per fetch: { members: { active, voided }, ... }
  const groups = useMemo(() => {
    const byRecordId = (a: Row, b: Row) =>
      String((b as { recordId?: string }).recordId || '')
        .localeCompare(String((a as { recordId?: string }).recordId || ''));

    const byDeleted = (a: Row, b: Row) =>
      new Date((b as VoidableRecord).deletedAt || 0).getTime() -
      new Date((a as VoidableRecord).deletedAt || 0).getTime();

    return keys.reduce<Record<string, LedgerGroup<Row>>>((acc, k) => {
      const mine = rows.filter((r) => r.__key === k);
      const field = config[k].statusField || 'status';
      const statusOf = (r: Row) => (r as unknown as Record<string, unknown>)[field];
      acc[k] = {
        active: mine.filter((r) => statusOf(r) !== 'VOID').sort(byRecordId),
        voided: mine.filter((r) => statusOf(r) === 'VOID').sort(byDeleted)
      };
      return acc;
    }, {});
  }, [rows, keys, config]);

  const active = useCallback(
    <K extends Key>(key: K) => (groups[key]?.active || []) as Array<RowOf<C, K>>,
    [groups]
  );

  const voided = useCallback(
    <K extends Key>(key: K) => (groups[key]?.voided || []) as Array<RowOf<C, K>>,
    [groups]
  );

  // Sequence across active + voided so a voided row never frees its number.
  // Reads the highest number already issued rather than counting rows — see
  // the note on nextSequence in utils/records.
  const nextSequence = useCallback(
    <K extends Key>(key: K, base = 1000) => {
      const { prefix } = config[key];
      const mine = rows.filter((r) => r.__key === key) as unknown as Array<Record<string, unknown>>;
      return nextSequenceFrom(mine, prefix, base);
    },
    [config, rows]
  );

  const save = useCallback(
    async <K extends Key>(
      key: K,
      payload: Partial<RowOf<C, K>>,
      editingRawId: number | string | null = null,
      together?: { operations: BatchOperation[]; summary: string }
    ): Promise<boolean> => {
      const { resource, label } = config[key];
      // Screens often build a payload by spreading a fetched row; the tags
      // this hook added are not columns and must not travel back.
      const { __key: _k, __raw: _r, recordType: _t, rawId: _i, ...fields } =
        payload as Record<string, unknown>;
      setSaving(true);
      try {
        const editing = editingRawId !== null && editingRawId !== undefined;
        const existing = editing ? rows.find((r) => r.__key === key && r.rawId === editingRawId)?.__raw : null;
        const full = (editing ? { ...(existing || {}), ...fields, id: editingRawId } : fields) as Partial<RowOf<C, K>>;
        if (together?.operations.length) {
          await batch.run([
            editing ? batch.change(resource, editingRawId, full) : batch.add(resource, full),
            ...together.operations
          ], together.summary);
          announce(`${label} ${editing ? 'updated' : 'saved'} successfully.`, 'success');
        } else if (editing) {
          await resource.update(editingRawId, full);
          announce(`${label} updated successfully.`, 'success');
        } else {
          await resource.create(fields as Partial<RowOf<C, K>>);
          announce(`${label} saved successfully.`, 'success');
        }
        await refetch();
        return true;
      } catch (err) {
        announce((err as Error).message || `Could not save the ${label.toLowerCase()}.`, 'error');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [config, rows, announce, refetch]
  );

  const requestVoid = useCallback((row: Row) => setVoiding(row), []);
  const cancelVoid = useCallback(() => setVoiding(null), []);

  const confirmVoid = useCallback(
    async (
      reason: string,
      onVoided?: (row: Row) => void | Promise<void>
    ): Promise<boolean> => {
      const item = voiding;
      if (!item) return false;
      setVoiding(null);

      const entry = config[item.__key];
      try {
        const payload: Record<string, unknown> = {
          ...(item as unknown as Record<string, unknown>),
          id: item.rawId,
          status: 'VOID',
          deleteReason: reason,
          deletedAt: stamp()
        };
        if (entry.statusField) payload[entry.statusField] = 'VOID';

        await entry.resource.update(item.rawId as number, payload);
        if (typeof onVoided === 'function') await onVoided(item);
        await refetch();
        announce(`${entry.label} deleted.`, 'success');
        return true;
      } catch (err) {
        announce((err as Error).message || 'Could not delete the record.', 'error');
        return false;
      }
    },
    [voiding, config, announce, refetch]
  );

  return {
    rows, setRows, groups, active, voided,
    loading, error, missing, refetch,
    saving, save, nextSequence,
    voiding, requestVoid, cancelVoid, confirmVoid
  };
};

export default useMultiLedger;
